/**
 * AI Interviews for BFSI Hiring | Viriqo Hires
 * Viriqo Hires - /solutions/bfsi
 *
 * Page body only (hero to final CTA). Header, footer and cookie banner are NOT included.
 * Markup, CSS (injected via <style>) and behaviour all live in this one file.
 * Usage:  import BfsiSolutionPage from "./BfsiSolutionPage";  <BfsiSolutionPage />
 * Requires only React 17+.
 */
import React, { useEffect } from "react";

/* ============================== CSS ============================== */
const CSS = String.raw`
/* ---- isolation: the page renders the same inside any app (host text-align, resets, framework defaults) ---- */
.vh-sol{display:block;width:100%;max-width:none;float:none;margin:0;padding:0;text-align:left;direction:ltr;font-size:16px;font-weight:400;font-style:normal;letter-spacing:normal;text-transform:none;text-indent:0;white-space:normal;word-spacing:normal;overflow-x:clip}
.vh-sol :is(h1,h2,h3,h4,h5,h6,p,li,summary,small,label){text-align:inherit}
.vh-sol{
  /* VLink website palette */
  --blue:#00AEEF;        /* VLink Blue - primary: buttons, links, active states */
  --purple:#6A1B9A;      /* VLink Purple - secondary: highlights, icons, interactive */
  --navy:#0B1F44;        /* Deep Navy - headings, navigation, footer, key text */
  --sky:#4FC3F7;         /* Sky Blue - icons, borders, subtle elements */
  --lviolet:#E9D8FF;     /* Light Violet - background accents, illustrations */
  --cool:#F5F8FF;        /* Cool Gray - section backgrounds, inputs */
  --gray:#D1D5DB;        /* Gray - borders, dividers */
  --slate:#6B7280;       /* Slate Gray - secondary text, labels */
  --grad:linear-gradient(100deg,#00AEEF 0%,#6A1B9A 100%);   /* Blue to Purple - hero, CTAs */
  --grad-cta:linear-gradient(125deg,#00AEEF 0%,#3A5BC7 45%,#6A1B9A 90%);        /* CTA buttons: 35deg diagonal (CSS 125deg = top-left to bottom-right) */
  --grad-cta-hover:linear-gradient(125deg,#00AEEF 0%,#3A4AB0 40%,#4A1270 85%);  /* CTA hover: same angle, deeper purple (#4A1270 = VLink Purple darkened) */
  --grad-dark:linear-gradient(135deg,#0B1F44 0%,#0B1F44 45%,#0A4F86 100%); /* aliases used throughout the stylesheet */
  --violet:var(--blue);
  --ink:var(--navy);
  --lilac:var(--cool);
  --ink-2:#4B5563;
  --ink-3:var(--slate);
  --line:#E3E7EE;
  --white:#fff;
  --radius:20px;
  --ease:cubic-bezier(.22,1,.36,1);
  --wrap:1200px;
  --pad:clamp(96px,12vw,160px);
}
.vh-sol,.vh-sol *,.vh-sol *::before,.vh-sol *::after{box-sizing:border-box;margin:0;padding:0}

.vh-sol{font-family:'Inter',system-ui,-apple-system,sans-serif;color:var(--ink);background:#fff;line-height:1.6;-webkit-font-smoothing:antialiased;overflow-x:clip}
.vh-sol img{max-width:100%;display:block}
.vh-sol a{color:inherit;text-decoration:none}
.vh-sol button{font:inherit;cursor:pointer;border:0;background:none;color:inherit}
.vh-sol .wrap{max-width:var(--wrap);margin:0 auto;padding:0 24px}
.vh-sol section{padding:var(--pad) 0;position:relative}

.vh-sol .eyebrow{display:inline-flex;align-items:center;gap:10px;font-size:13px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;color:var(--purple)}
.vh-sol .eyebrow::before{content:"";width:22px;height:2px;border-radius:2px;background:linear-gradient(90deg,var(--purple),var(--violet))}
.vh-sol h1,.vh-sol h2,.vh-sol h3{letter-spacing:-.025em;line-height:1.12;color:var(--ink)}
.vh-sol h1{font-size:clamp(38px,5.2vw,64px);font-weight:800}
.vh-sol h2{font-size:clamp(30px,4vw,50px);font-weight:700;margin-top:18px}
.vh-sol h3{font-size:20px;font-weight:600;letter-spacing:-.01em}
.vh-sol .lead{font-size:clamp(17px,1.5vw,20px);color:var(--ink-2);max-width:640px;margin-top:22px}
.vh-sol .center{text-align:center}
.vh-sol .center .lead{margin-left:auto;margin-right:auto}
.vh-sol .grad{background:linear-gradient(100deg,var(--purple) 10%,var(--violet) 90%);-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-fill-color:transparent}
.vh-sol .sec-head{max-width:780px;margin-bottom:clamp(56px,7vw,88px)}
.vh-sol .sec-head.center{margin-left:auto;margin-right:auto}

.vh-sol .btn{display:inline-flex;align-items:center;justify-content:center;gap:10px;height:54px;padding:0 28px;border-radius:999px;font-weight:600;font-size:16px;transition:transform .35s var(--ease),box-shadow .35s var(--ease),background .3s,border-color .3s;position:relative;overflow:hidden;white-space:nowrap}
.vh-sol .btn-primary{background:var(--purple);color:#fff;box-shadow:0 10px 30px -10px rgba(106,27,154,.6)}
.vh-sol .btn-primary::after{content:"";position:absolute;inset:0;background:linear-gradient(110deg,transparent 30%,rgba(255,255,255,.35) 50%,transparent 70%);transform:translateX(-120%);transition:transform .8s var(--ease)}
.vh-sol .btn-primary:hover::after{transform:translateX(120%)}
.vh-sol .btn-primary:hover{transform:translateY(-2px);box-shadow:0 18px 40px -12px rgba(106,27,154,.7)}
.vh-sol .btn-ghost{background:#fff;color:var(--ink);border:1.5px solid var(--line)}
.vh-sol .btn-ghost:hover{border-color:var(--violet);transform:translateY(-2px)}
.vh-sol .btn .arr{transition:transform .35s var(--ease)}
.vh-sol .btn:hover .arr{transform:translateX(4px)}

/* reveal */
.vh-sol .rv{opacity:0;transform:translateY(36px);transition:opacity 1s var(--ease),transform 1s var(--ease)}
.vh-sol .rv.in{opacity:1;transform:none}
.vh-sol .stagger>*{opacity:0;transform:translateY(36px);transition:opacity .9s var(--ease),transform .9s var(--ease)}
.vh-sol .stagger.in>*{opacity:1;transform:none}
.vh-sol .stagger.in>*:nth-child(2){transition-delay:.1s}.vh-sol .stagger.in>*:nth-child(3){transition-delay:.2s}
.vh-sol .stagger.in>*:nth-child(4){transition-delay:.3s}.vh-sol .stagger.in>*:nth-child(5){transition-delay:.4s}.vh-sol .stagger.in>*:nth-child(6){transition-delay:.5s}
.vh-sol .fade-in{opacity:0;animation:rise 1s var(--ease) forwards}
@keyframes rise{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:none}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes bob{50%{transform:translateY(-10px)}}
@keyframes scroll{to{transform:translateX(-50%)}}
@keyframes ping{0%{box-shadow:0 0 0 0 rgba(0,174,239,.55)}70%{box-shadow:0 0 0 10px rgba(0,174,239,0)}100%{box-shadow:0 0 0 0 rgba(0,174,239,0)}}

.vh-sol .ic{width:56px;height:56px;border-radius:16px;background:var(--lilac);display:grid;place-items:center;color:var(--purple);transition:background .4s,color .4s,transform .5s var(--ease);flex:none}
.vh-sol .ic svg{width:26px;height:26px}

/* ================= HERO ================= */
.vh-sol .hero{padding:clamp(150px,16vw,190px) 0 clamp(72px,8vw,100px);overflow:hidden;background:radial-gradient(900px 520px at 90% 0%,#F5F8FF 0%,rgba(245,248,255,0) 60%),#fff}
.vh-sol .hero .blob{position:absolute;width:420px;height:420px;border-radius:50%;background:rgba(0,174,239,.18);filter:blur(80px);left:-160px;bottom:-120px;animation:bob 12s ease-in-out infinite}
.vh-sol .hero-grid{position:relative;display:grid;grid-template-columns:1.05fr .95fr;gap:clamp(48px,6vw,90px);align-items:center}
.vh-sol .hero h1{margin-top:22px}
.vh-sol .hero .ctas{display:flex;gap:16px;flex-wrap:wrap;margin-top:44px}
.vh-sol .pills{display:flex;gap:12px;flex-wrap:wrap;margin-top:48px}
.vh-sol .pill{display:inline-flex;align-items:center;gap:10px;padding:10px 18px;border-radius:999px;background:#fff;border:1px solid var(--line);font-size:14px;font-weight:500;color:var(--ink-2)}
.vh-sol .pill i{width:8px;height:8px;border-radius:50%;background:var(--violet);animation:ping 2.4s infinite}
.vh-sol .pill:nth-child(2) i{animation-delay:.8s}.vh-sol .pill:nth-child(3) i{animation-delay:1.6s}
/* configurator mock */
.vh-sol .config{position:relative;max-width:520px;margin:0 auto;width:100%}
.vh-sol .panel{background:#fff;border:1px solid var(--line);border-radius:24px;padding:28px;box-shadow:0 50px 90px -50px rgba(106,27,154,.5)}
.vh-sol .panel-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:24px}
.vh-sol .panel-top b{font-size:15px}
.vh-sol .panel-top span{font-size:12px;font-weight:600;color:#12805C;background:#E5F7EF;border-radius:999px;padding:4px 10px;display:inline-flex;align-items:center;gap:6px}
.vh-sol .panel-top span::before{content:"";width:6px;height:6px;border-radius:50%;background:#12A06E}
.vh-sol .field{margin-bottom:22px}
.vh-sol .field label{display:block;font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--ink-3);margin-bottom:10px}
.vh-sol .select{height:50px;border-radius:12px;border:1.5px solid var(--line);display:flex;align-items:center;gap:12px;padding:0 16px;font-weight:600;overflow:hidden}
.vh-sol .select .ind-ic{width:28px;height:28px;border-radius:8px;background:var(--lilac);display:grid;place-items:center;color:var(--purple)}
.vh-sol .select .ind-ic svg{width:16px;height:16px}
.vh-sol .select .val{position:relative;height:24px;flex:1}
.vh-sol .select .val em{position:absolute;inset:0;font-style:normal;transition:transform .5s var(--ease),opacity .5s}
.vh-sol .select .val em.out{transform:translateY(-100%);opacity:0}
.vh-sol .select .val em.pre{transform:translateY(100%);opacity:0}
.vh-sol .slider{height:8px;border-radius:8px;background:var(--lilac);position:relative}
.vh-sol .slider b{position:absolute;left:0;top:0;bottom:0;border-radius:8px;background:linear-gradient(90deg,var(--purple),var(--violet));width:30%;transition:width 1.2s var(--ease)}
.vh-sol .slider i{position:absolute;top:50%;width:20px;height:20px;margin:-10px 0 0 -10px;border-radius:50%;background:#fff;border:3px solid var(--purple);left:30%;transition:left 1.2s var(--ease);box-shadow:0 4px 12px rgba(106,27,154,.35)}
.vh-sol .slider-lbl{display:flex;justify-content:space-between;font-size:12px;color:var(--ink-3);margin-top:10px}
.vh-sol .toggles{display:flex;flex-wrap:wrap;gap:8px}
.vh-sol .tg{font-size:13px;font-weight:600;padding:8px 12px;border-radius:999px;border:1.5px solid var(--line);color:var(--ink-3);transition:all .4s var(--ease)}
.vh-sol .tg.on{background:var(--purple);border-color:var(--purple);color:#fff}
.vh-sol .floater{position:absolute;background:#fff;border:1px solid var(--line);border-radius:16px;padding:12px 16px;display:flex;align-items:center;gap:10px;font-size:13px;font-weight:600;box-shadow:0 20px 40px -20px rgba(106,27,154,.45);animation:bob 5s ease-in-out infinite}
.vh-sol .floater .d{width:30px;height:30px;border-radius:10px;display:grid;place-items:center;background:var(--purple);color:#fff}
.vh-sol .floater .d svg{width:16px;height:16px}
.vh-sol .floater.f1{left:-36px;top:-24px}
.vh-sol .floater.f2{right:-28px;bottom:-22px;animation-delay:-2.5s}
@media(max-width:980px){.vh-sol .hero-grid{grid-template-columns:1fr}.vh-sol .floater.f1{left:-8px}.vh-sol .floater.f2{right:-8px}}
@media(max-width:520px){.vh-sol .floater{display:none}.vh-sol .panel{padding:22px}}

/* industry strip */
.vh-sol .strip{padding:44px 0 52px;border-top:1px solid var(--line);border-bottom:1px solid var(--line)}
.vh-sol .marquee{overflow:hidden;-webkit-mask-image:linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent);mask-image:linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent)}
.vh-sol .marquee .track{display:flex;gap:72px;width:max-content;animation:scroll 34s linear infinite}
.vh-sol .marquee:hover .track{animation-play-state:paused}
.vh-sol .marquee span{display:inline-flex;align-items:center;gap:12px;font-size:22px;font-weight:700;color:#A7ACBA;letter-spacing:-.02em;transition:color .3s}
.vh-sol .marquee span svg{width:24px;height:24px}
.vh-sol .marquee span:hover{color:var(--purple)}

/* ================= INDUSTRIES ================= */
.vh-sol .ind-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:24px}
.vh-sol .ind{grid-column:span 2;position:relative;padding:40px 34px 34px;border-radius:24px;background:#fff;border:1px solid var(--line);overflow:hidden;display:flex;flex-direction:column;transition:transform .5s var(--ease),box-shadow .5s,border-color .5s}
.vh-sol .ind:nth-child(4),.vh-sol .ind:nth-child(5){grid-column:span 3}
.vh-sol .ind::before{content:"";position:absolute;inset:0;background:radial-gradient(420px circle at var(--mx,50%) var(--my,0%),rgba(0,174,239,.14),transparent 45%);opacity:0;transition:opacity .4s}
.vh-sol .ind:hover{transform:translateY(-8px);box-shadow:0 30px 60px -30px rgba(106,27,154,.35);border-color:rgba(0,174,239,.45)}
.vh-sol .ind:hover::before{opacity:1}
.vh-sol .ind:hover .ic{background:var(--purple);color:#fff;transform:rotate(-6deg)}
.vh-sol .ind .ic{margin-bottom:32px}
.vh-sol .ind p{color:var(--ink-2);margin-top:10px;flex:1}
.vh-sol .ind .more{margin-top:28px;display:inline-flex;align-items:center;gap:8px;font-weight:600;color:var(--purple);font-size:15px}
.vh-sol .ind .more b{display:inline-block;transition:transform .35s var(--ease)}
.vh-sol .ind:hover .more b{transform:translateX(6px)}
@media(max-width:980px){.vh-sol .ind-grid{grid-template-columns:1fr 1fr}.vh-sol .ind,.vh-sol .ind:nth-child(4),.vh-sol .ind:nth-child(5){grid-column:span 1}.vh-sol .ind:nth-child(5){grid-column:span 2}}
@media(max-width:600px){.vh-sol .ind-grid{grid-template-columns:1fr}.vh-sol .ind:nth-child(5){grid-column:span 1}}

/* ================= USE CASES (tabs) ================= */
.vh-sol .uc{background:var(--lilac);border-radius:48px;margin:0 16px}
.vh-sol .uc-grid{display:grid;grid-template-columns:.9fr 1.1fr;gap:clamp(40px,5vw,72px);align-items:center}
.vh-sol .tabs{display:grid;gap:10px}
.vh-sol .tab{position:relative;text-align:left;padding:22px 26px;border-radius:18px;background:transparent;transition:background .4s,box-shadow .4s;overflow:hidden}
.vh-sol .tab h3{font-size:18px;display:flex;align-items:center;gap:14px;color:var(--ink-3);transition:color .3s}
.vh-sol .tab h3 small{font-size:13px;font-weight:700;color:var(--violet)}
.vh-sol .tab p{color:var(--ink-2);font-size:15px;max-height:0;opacity:0;overflow:hidden;transition:max-height .5s var(--ease),opacity .4s,margin .4s;padding-left:34px}
.vh-sol .tab.on{background:#fff;box-shadow:0 20px 40px -28px rgba(106,27,154,.45)}
.vh-sol .tab.on h3{color:var(--ink)}
.vh-sol .tab.on p{max-height:120px;opacity:1;margin-top:8px}
.vh-sol .tab .prog{position:absolute;left:0;bottom:0;height:3px;width:0;background:linear-gradient(90deg,var(--purple),var(--violet))}
.vh-sol .tab.on .prog{animation:prog 6s linear forwards}
.vh-sol .uc.paused .tab.on .prog{animation-play-state:paused}
@keyframes prog{to{width:100%}}
.vh-sol .stage{position:relative;background:#fff;border-radius:28px;padding:36px;min-height:440px;box-shadow:0 40px 80px -50px rgba(106,27,154,.5);overflow:hidden}
.vh-sol .stage-title{font-size:13px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--ink-3)}
.vh-sol .stage h4{font-size:22px;letter-spacing:-.02em;margin-top:6px;min-height:30px}
.vh-sol .funnel{margin-top:30px;display:grid;gap:12px}
.vh-sol .fr{display:grid;grid-template-columns:110px 1fr 44px;align-items:center;gap:14px;font-size:13px;color:var(--ink-2)}
.vh-sol .fr .bar{height:14px;border-radius:14px;background:var(--lilac);overflow:hidden}
.vh-sol .fr .bar b{display:block;height:100%;border-radius:14px;background:linear-gradient(90deg,var(--purple),var(--violet));width:0;transition:width 1.1s var(--ease)}
.vh-sol .fr em{font-style:normal;font-weight:700;color:var(--ink);text-align:right}
.vh-sol .callouts{position:absolute;right:24px;bottom:24px;display:grid;gap:10px;justify-items:end}
.vh-sol .co{display:inline-flex;align-items:center;gap:10px;padding:10px 16px;border-radius:999px;background:var(--ink);color:#fff;font-size:13px;font-weight:600;box-shadow:0 14px 30px -14px rgba(11,31,68,.6);animation:bob 5s ease-in-out infinite}
.vh-sol .co:nth-child(2){animation-delay:-2.5s;background:var(--purple)}
.vh-sol .co svg{width:16px;height:16px;color:var(--violet)}
.vh-sol .co:nth-child(2) svg{color:#fff}
@media(max-width:980px){.vh-sol .uc-grid{grid-template-columns:1fr}.vh-sol .stage{min-height:0;padding:28px}.vh-sol .callouts{position:static;justify-items:start;margin-top:28px}}
@media(max-width:480px){.vh-sol .fr{grid-template-columns:86px 1fr 36px;gap:10px}}

/* ================= WORKFLOW ================= */
.vh-sol .wf{display:grid;grid-template-columns:repeat(5,1fr);gap:20px;position:relative}
.vh-sol .wf .line{position:absolute;top:44px;left:10%;right:10%;height:2px;background:var(--line)}
.vh-sol .wf .line b{position:absolute;inset:0;background:linear-gradient(90deg,var(--purple),var(--violet));transform:scaleX(0);transform-origin:left;transition:transform .8s var(--ease)}
.vh-sol .st{text-align:center;padding:0 6px}
.vh-sol .st .n{position:relative;width:88px;height:88px;margin:0 auto 28px;border-radius:24px;background:#fff;border:1px solid var(--line);display:grid;place-items:center;color:var(--purple);transition:all .6s var(--ease);box-shadow:0 18px 40px -26px rgba(106,27,154,.5)}
.vh-sol .st .n svg{width:32px;height:32px}
.vh-sol .st .n em{position:absolute;top:-10px;left:50%;transform:translateX(-50%);font-style:normal;font-size:11px;font-weight:700;background:#fff;border:1px solid var(--line);border-radius:999px;padding:2px 8px;color:var(--ink-3);transition:all .6s}
.vh-sol .st.on .n{background:var(--purple);color:#fff;border-color:var(--purple);transform:translateY(-6px) scale(1.04);box-shadow:0 24px 50px -20px rgba(106,27,154,.7)}
.vh-sol .st.on .n em{background:var(--violet);border-color:var(--violet);color:#fff}
.vh-sol .st p{color:var(--ink-2);margin-top:8px;font-size:15px}
@media(max-width:980px){.vh-sol .wf{grid-template-columns:1fr;max-width:480px;margin:0 auto;gap:0}
  .vh-sol .wf .line{top:44px;bottom:44px;left:43px;right:auto;width:2px;height:auto}
  .vh-sol .wf .line b{transform:scaleY(0);transform-origin:top}
  .vh-sol .st{display:grid;grid-template-columns:88px 1fr;gap:24px;text-align:left;align-items:center;padding:0 0 32px}
  .vh-sol .st:last-child{padding-bottom:0}.vh-sol .st .n{margin:0}}

/* ================= SPOTLIGHT ================= */
.vh-sol .spot{background:var(--ink);color:#fff;border-radius:48px;margin:0 16px;overflow:hidden}
.vh-sol .spot h2{color:#fff}.vh-sol .spot .lead{color:rgba(255,255,255,.72)}.vh-sol .spot .eyebrow{color:var(--violet)}
.vh-sol .spot-grid{display:grid;grid-template-columns:.85fr 1.15fr;gap:clamp(48px,6vw,90px);align-items:center}
.vh-sol .spot .btn{margin-top:40px}
.vh-sol .spot .btn-light{background:#fff;color:var(--purple)}
.vh-sol .mock{display:grid;grid-template-columns:1fr 1fr;gap:18px;align-items:start}
.vh-sol .wlist{background:#16294F;border:1px solid rgba(255,255,255,.08);border-radius:22px;padding:22px}
.vh-sol .wlist h5,.vh-sol .cand h5{font-size:12px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:rgba(255,255,255,.55);margin-bottom:14px}
.vh-sol .wi{display:flex;align-items:center;gap:12px;padding:12px;border-radius:12px;font-size:14px;color:rgba(255,255,255,.55);transition:all .5s var(--ease)}
.vh-sol .wi .ck{width:22px;height:22px;border-radius:50%;border:1.5px solid rgba(255,255,255,.25);display:grid;place-items:center;flex:none;transition:all .5s var(--ease)}
.vh-sol .wi .ck svg{width:12px;height:12px;opacity:0;transform:scale(.4);transition:all .4s var(--ease)}
.vh-sol .wi.done{color:#fff}
.vh-sol .wi.done .ck{background:var(--violet);border-color:var(--violet)}
.vh-sol .wi.done .ck svg{opacity:1;transform:none}
.vh-sol .wi.cur{background:rgba(0,174,239,.14);color:#fff}
.vh-sol .wi.cur .ck{border-color:var(--violet);animation:ping 1.6s infinite}
.vh-sol .cand{background:#fff;color:var(--ink);border-radius:22px;padding:24px;margin-top:40px;box-shadow:0 40px 80px -40px rgba(0,0,0,.6)}
.vh-sol .cand h5{color:var(--ink-3)}
.vh-sol .person{display:flex;align-items:center;gap:12px}
.vh-sol .person .av{width:44px;height:44px;border-radius:50%;background:linear-gradient(135deg,var(--purple),var(--violet));color:#fff;display:grid;place-items:center;font-weight:700;font-size:15px}
.vh-sol .person b{display:block;font-size:16px}
.vh-sol .person span{font-size:13px;color:var(--ink-3)}
.vh-sol .ringwrap{position:relative;width:132px;height:132px;margin:22px auto 18px}
.vh-sol .ringwrap svg{width:100%;height:100%;transform:rotate(-90deg)}
.vh-sol .ringwrap circle{fill:none;stroke-width:10}
.vh-sol .ringwrap .bg{stroke:var(--lilac)}
.vh-sol .ringwrap .fg{stroke:url(#rg);stroke-linecap:round;stroke-dasharray:339.3;stroke-dashoffset:339.3;transition:stroke-dashoffset 1.8s var(--ease)}
.vh-sol .ringwrap .val{position:absolute;inset:0;display:grid;place-items:center;text-align:center}
.vh-sol .ringwrap .val b{font-size:30px;font-weight:800;letter-spacing:-.03em;display:block;line-height:1}
.vh-sol .ringwrap .val .lbl{display:block;font-size:11px;color:var(--ink-3);font-weight:600;margin-top:4px}
.vh-sol .sk{margin-top:12px}
.vh-sol .sk div{display:flex;justify-content:space-between;font-size:13px;color:var(--ink-2);margin-bottom:6px}
.vh-sol .sk div b{color:var(--ink)}
.vh-sol .sk i{display:block;height:6px;border-radius:6px;background:var(--lilac);overflow:hidden}
.vh-sol .sk i u{display:block;height:100%;width:0;border-radius:6px;background:linear-gradient(90deg,var(--purple),var(--violet));transition:width 1.4s var(--ease)}
.vh-sol .overall{display:flex;justify-content:space-between;align-items:center;margin-top:18px;padding-top:14px;border-top:1px solid var(--line);font-size:14px;font-weight:600}
.vh-sol .overall b{font-size:18px;color:var(--purple)}
@media(max-width:980px){.vh-sol .spot-grid{grid-template-columns:1fr}}
@media(max-width:600px){.vh-sol .mock{grid-template-columns:1fr}.vh-sol .cand{margin-top:0}}

/* ================= CHALLENGE BAND ================= */
.vh-sol .band{padding:var(--pad) 0 0}
.vh-sol .band-inner{border-radius:28px;padding:clamp(40px,5vw,56px) clamp(28px,5vw,64px);background:var(--lilac);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
.vh-sol .band-inner::before{content:"";position:absolute;width:260px;height:260px;border-radius:50%;border:1.5px dashed rgba(0,174,239,.45);right:-80px;top:-120px;animation:spin 30s linear infinite}
.vh-sol .band-inner h3{font-size:clamp(24px,2.6vw,32px);font-weight:700;letter-spacing:-.02em}
.vh-sol .band-inner p{color:var(--ink-2);margin-top:8px;font-size:17px}
.vh-sol .band-inner .btn{position:relative}

/* ================= TEAMS ================= */
.vh-sol .team-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px}
.vh-sol .team{background:#fff;border:1px solid var(--line);border-radius:24px;padding:48px 36px;position:relative;overflow:hidden;transition:transform .5s var(--ease),box-shadow .5s}
.vh-sol .team::after{content:"";position:absolute;left:0;right:0;bottom:0;height:4px;background:linear-gradient(90deg,var(--purple),var(--violet));transform:scaleX(0);transform-origin:left;transition:transform .6s var(--ease)}
.vh-sol .team:hover{transform:translateY(-8px);box-shadow:0 30px 60px -34px rgba(106,27,154,.4)}
.vh-sol .team:hover::after{transform:scaleX(1)}
.vh-sol .team:hover .ic{background:var(--purple);color:#fff}
.vh-sol .team .ic{margin-bottom:32px}
.vh-sol .team p{color:var(--ink-2);margin-top:12px}
@media(max-width:900px){.vh-sol .team-grid{grid-template-columns:1fr}}

/* ================= FINAL CTA ================= */
.vh-sol .final{padding-top:0}
.vh-sol .cta-inner{border-radius:40px;padding:clamp(72px,9vw,120px) clamp(28px,6vw,90px);background:linear-gradient(125deg,var(--purple) 0%,#3A5BC7 55%,var(--violet) 120%);color:#fff;position:relative;overflow:hidden;text-align:center}
.vh-sol .cta-inner h2{color:#fff;max-width:860px;margin:0 auto}
.vh-sol .cta-inner .lead{color:rgba(255,255,255,.85);margin-left:auto;margin-right:auto}
.vh-sol .cta-inner .btn{margin-top:44px;background:#fff;color:var(--purple);box-shadow:0 14px 30px -12px rgba(0,0,0,.35)}
.vh-sol .cta-inner .btn::after{background:linear-gradient(110deg,transparent 30%,rgba(0,174,239,.25) 50%,transparent 70%)}
.vh-sol .cta-inner .ring{position:absolute;border-radius:50%;border:1px solid rgba(255,255,255,.18);animation:spin 40s linear infinite}
.vh-sol .cta-inner .r1{width:560px;height:560px;left:-180px;bottom:-280px;border-style:dashed}
.vh-sol .cta-inner .r2{width:420px;height:420px;right:-140px;top:-180px;animation-direction:reverse}
.vh-sol .cta-inner .c{position:relative;z-index:2}

/* ---------- buttons ---------- */
.vh-sol .btn{display:inline-flex;align-items:center;gap:10px;height:54px;padding:0 28px;border-radius:999px;font-weight:600;font-size:16px;transition:transform .35s var(--ease),box-shadow .35s var(--ease),background .3s;cursor:pointer;border:0;position:relative;overflow:hidden;white-space:nowrap}
.vh-sol .btn-primary{transition:transform .35s var(--ease),box-shadow .35s var(--ease);background:var(--grad-cta);color:#fff;box-shadow:0 10px 30px -10px rgba(106,27,154,.6);isolation:isolate}
.vh-sol .btn-primary::before{content:"";position:absolute;inset:0;z-index:-1;border-radius:inherit;background:var(--grad-cta-hover);opacity:0;transition:opacity .45s var(--ease)}
.vh-sol .btn-primary:hover::before{opacity:1}
.vh-sol .btn-primary::after{content:"";position:absolute;inset:0;background:linear-gradient(110deg,transparent 30%,rgba(255,255,255,.35) 50%,transparent 70%);transform:translateX(-120%);transition:transform .8s var(--ease)}
.vh-sol .btn-primary:hover::after{transform:translateX(120%)}
.vh-sol .btn-primary:hover{transform:translateY(-2px);box-shadow:0 18px 40px -12px rgba(74,18,112,.75)}
.vh-sol .btn-ghost{background:#fff;color:var(--ink);border:1.5px solid var(--line)}
.vh-sol .btn-ghost:hover{border-color:var(--violet);transform:translateY(-2px)}
.vh-sol .btn .arr{transition:transform .35s var(--ease)}
.vh-sol .btn:hover .arr{transform:translateX(4px)}
.vh-sol .play{width:26px;height:26px;border-radius:50%;background:var(--lilac);display:grid;place-items:center}
.vh-sol .play svg{width:10px;height:10px;fill:var(--purple);margin-left:2px}

/* ---------- footer ---------- */

.vh-sol .grad{background:linear-gradient(100deg,var(--blue) 0%,var(--purple) 85%);-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-fill-color:transparent}
.vh-sol .cta-inner .btn-primary::before,.vh-sol .band-inner .btn-primary::before{display:none}
.vh-sol .spot,.vh-sol .philo,.vh-sol .integ{background:var(--grad-dark)!important}
.vh-sol .readbar{top:72px}

@media (prefers-reduced-motion:reduce){
  .vh-sol,.vh-sol *,.vh-sol *::before,.vh-sol *::after{animation:none!important;transition:none!important}
  .vh-sol .rv,.vh-sol .stagger>*,.vh-sol .fade-in{opacity:1!important;transform:none!important}
}

/* ================= BFSI page ================= */
.vh-sol .b-config .select .val em{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;right:0}
@media(max-width:520px){.vh-sol .b-config .select{font-size:14px;gap:10px;padding:0 12px}.vh-sol .b-type{font-size:11px;padding:4px 8px}}

.vh-sol .b-hero h1{font-size:clamp(36px,4.6vw,58px)}
.vh-sol .hero .blob.b2{left:auto;right:-120px;top:-80px;bottom:auto;background:rgba(106,27,154,.12);animation-delay:-6s}
.vh-sol .b-type{font-size:12px;font-weight:700;padding:5px 10px;border-radius:999px;background:var(--lviolet,#E9D8FF);color:var(--purple);transition:background .4s,color .4s;white-space:nowrap}
.vh-sol .b-type.tech{background:#E0F5FD;color:#0A6E99}
.vh-sol .b-skills{display:grid;gap:12px}
.vh-sol .b-sk{display:grid;grid-template-columns:150px 1fr 40px;align-items:center;gap:12px;font-size:13px;color:var(--ink-2)}
.vh-sol .b-sk .bar{height:8px;border-radius:8px;background:var(--cool,#F5F8FF);overflow:hidden;border:1px solid var(--line)}
.vh-sol .b-sk .bar b{display:block;height:100%;border-radius:8px;background:var(--grad-cta);width:0;transition:width .9s var(--ease)}
.vh-sol .b-sk em{font-style:normal;font-weight:700;color:var(--ink);text-align:right}
@media(max-width:520px){.vh-sol .b-sk{grid-template-columns:110px 1fr 36px}}

.vh-sol .b-num{font-size:13px;font-weight:700;color:var(--blue);letter-spacing:.04em}
.vh-sol .b-grid4{display:grid;grid-template-columns:repeat(4,1fr);gap:24px}
.vh-sol .b-grid3{display:grid;grid-template-columns:repeat(3,1fr);gap:24px}
.vh-sol .b-card,.vh-sol .b-cap{position:relative;background:#fff;border:1px solid var(--line);border-radius:24px;padding:34px 30px 32px;transition:transform .5s var(--ease),box-shadow .5s,border-color .5s;overflow:hidden}
.vh-sol .b-card .b-num{position:absolute;top:30px;right:30px}
.vh-sol .b-card .ic,.vh-sol .b-cap .ic{margin-bottom:28px}
.vh-sol .b-card p,.vh-sol .b-cap p{color:var(--ink-2);margin-top:10px;font-size:15.5px}
.vh-sol .b-card::after,.vh-sol .b-cap::after{content:"";position:absolute;left:0;right:0;bottom:0;height:3px;background:var(--grad-cta);transform:scaleX(0);transform-origin:left;transition:transform .6s var(--ease)}
.vh-sol .b-card:hover,.vh-sol .b-cap:hover{transform:translateY(-8px);box-shadow:0 30px 60px -30px rgba(106,27,154,.35);border-color:rgba(0,174,239,.45)}
.vh-sol .b-card:hover::after,.vh-sol .b-cap:hover::after{transform:scaleX(1)}
.vh-sol .b-card:hover .ic,.vh-sol .b-cap:hover .ic{background:var(--purple);color:#fff;transform:rotate(-6deg)}
@media(max-width:1080px){.vh-sol .b-grid4{grid-template-columns:1fr 1fr}.vh-sol .b-grid3{grid-template-columns:1fr 1fr}}
@media(max-width:620px){.vh-sol .b-grid4,.vh-sol .b-grid3{grid-template-columns:1fr}}

.vh-sol .b-challenge{padding-top:clamp(72px,9vw,120px)}
.vh-sol .b-roles{background:var(--cool,#F5F8FF);border-radius:48px;margin:0 16px}
.vh-sol .b-rolegrid{display:grid;grid-template-columns:1fr 1fr;gap:28px}
.vh-sol .b-rolecard{background:#fff;border-radius:28px;padding:40px 38px;border:1px solid var(--line);box-shadow:0 40px 80px -60px rgba(11,31,68,.45);display:flex;flex-direction:column;transition:transform .5s var(--ease),box-shadow .5s}
.vh-sol .b-rolecard:hover{transform:translateY(-6px);box-shadow:0 40px 80px -40px rgba(106,27,154,.4)}
.vh-sol .b-rolehead{display:flex;gap:20px;align-items:flex-start;padding-bottom:26px;border-bottom:1px solid var(--line)}
.vh-sol .b-rolehead p{color:var(--ink-2);margin-top:8px;font-size:15.5px}
.vh-sol .b-rolecard.alt .ic{background:var(--purple);color:#fff}
.vh-sol .b-rolecard:not(.alt) .ic{background:var(--blue);color:#fff}
.vh-sol .b-list{list-style:none;display:grid;gap:14px;margin:26px 0 28px;flex:1}
.vh-sol .b-list li{display:flex;gap:12px;align-items:flex-start;font-size:16px;color:var(--ink)}
.vh-sol .b-list .tick{width:22px;height:22px;border-radius:50%;background:#E0F5FD;color:#0A8FC4;display:grid;place-items:center;flex:none;margin-top:1px}
.vh-sol .b-rolecard.alt .b-list .tick{background:var(--lviolet,#E9D8FF);color:var(--purple)}
.vh-sol .b-list .tick svg{width:12px;height:12px}
.vh-sol .b-tags{display:flex;flex-wrap:wrap;gap:8px}
.vh-sol .b-tags span{font-size:13px;font-weight:600;padding:7px 12px;border-radius:999px;border:1.5px solid var(--line);color:var(--ink-3)}
.vh-sol .b-note{max-width:760px;margin:44px auto 0;text-align:center;font-size:15px;color:var(--ink-3)}
@media(max-width:900px){.vh-sol .b-rolegrid{grid-template-columns:1fr}.vh-sol .b-rolecard{padding:32px 26px}.vh-sol .b-roles{border-radius:32px;margin:0 8px}}

.vh-sol .b-after{margin-top:64px}

.vh-sol .b-trust{background:var(--grad-dark);color:#fff;border-radius:48px;margin:0 16px;overflow:hidden}
.vh-sol .b-trust::before{content:"";position:absolute;width:520px;height:520px;border-radius:50%;right:-180px;top:-200px;background:radial-gradient(closest-side,rgba(0,174,239,.28),transparent);pointer-events:none}
.vh-sol .b-trust-grid{display:grid;grid-template-columns:.9fr 1.1fr;gap:clamp(40px,6vw,90px);align-items:center;position:relative}
.vh-sol .b-trust h2{color:#fff}
.vh-sol .b-trust .lead{color:rgba(255,255,255,.82)}
.vh-sol .eyebrow.light{color:#7FD8F7}
.vh-sol .eyebrow.light::before{background:linear-gradient(90deg,#7FD8F7,#fff)}
.vh-sol .b-fine{margin-top:28px;font-size:14.5px;color:rgba(255,255,255,.65);padding-left:16px;border-left:2px solid rgba(0,174,239,.6);max-width:520px}
.vh-sol .b-govlist{display:grid;gap:14px}
.vh-sol .b-gov{display:flex;gap:18px;align-items:flex-start;padding:22px 24px;border-radius:20px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12);transition:background .4s,transform .5s var(--ease),border-color .4s}
.vh-sol .b-gov:hover{background:rgba(255,255,255,.11);transform:translateX(6px);border-color:rgba(0,174,239,.55)}
.vh-sol .b-gov .ic{width:48px;height:48px;border-radius:14px;background:rgba(0,174,239,.16);color:#7FD8F7}
.vh-sol .b-gov .ic svg{width:22px;height:22px}
.vh-sol .b-gov h3{color:#fff;font-size:18px}
.vh-sol .b-gov p{color:rgba(255,255,255,.75);margin-top:6px;font-size:15px}
@media(max-width:980px){.vh-sol .b-trust-grid{grid-template-columns:1fr}.vh-sol .b-trust{border-radius:32px;margin:0 8px}}

.vh-sol .b-scen-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:24px}
.vh-sol .b-sc{grid-column:span 2;position:relative;padding:34px 30px;border-radius:24px;background:linear-gradient(180deg,#fff,#F7F9FF);border:1px solid var(--line);transition:transform .5s var(--ease),box-shadow .5s}
.vh-sol .b-sc:nth-child(4),.vh-sol .b-sc:nth-child(5){grid-column:span 3}
.vh-sol .b-sc .b-num{display:block;margin-bottom:18px}
.vh-sol .b-sc .ic{position:absolute;top:28px;right:28px;width:48px;height:48px;border-radius:14px}
.vh-sol .b-sc .ic svg{width:22px;height:22px}
.vh-sol .b-sc h3{max-width:80%}
.vh-sol .b-sc p{color:var(--ink-2);margin-top:12px;font-size:15.5px}
.vh-sol .b-sc:hover{transform:translateY(-8px);box-shadow:0 30px 60px -30px rgba(106,27,154,.35)}
.vh-sol .b-sc:hover .ic{background:var(--purple);color:#fff}
@media(max-width:980px){.vh-sol .b-scen-grid{grid-template-columns:1fr 1fr}.vh-sol .b-sc,.vh-sol .b-sc:nth-child(4){grid-column:span 1}.vh-sol .b-sc:nth-child(5){grid-column:span 2}}
@media(max-width:620px){.vh-sol .b-scen-grid{grid-template-columns:1fr}.vh-sol .b-sc:nth-child(5){grid-column:span 1}}

.vh-sol .b-teams{padding-top:0}
.vh-sol .b-band-sec{padding:0 0 var(--pad)}
.vh-sol .b-band{display:flex;align-items:center;justify-content:space-between;gap:32px;padding:44px 52px;border-radius:28px;background:linear-gradient(#fff,#fff) padding-box,var(--grad-cta) border-box;border:2px solid transparent;box-shadow:0 30px 70px -45px rgba(106,27,154,.5)}
.vh-sol .b-band h2{font-size:clamp(24px,2.6vw,32px);margin:0}
.vh-sol .b-band p{color:var(--ink-2);margin-top:10px}
@media(max-width:820px){.vh-sol .b-band{flex-direction:column;align-items:flex-start;padding:34px 28px}}

.vh-sol .b-faq{padding-top:0}
.vh-sol .b-faq-grid{display:grid;grid-template-columns:.8fr 1.2fr;gap:clamp(40px,6vw,80px);align-items:start}
.vh-sol .b-faq .sec-head{position:sticky;top:120px;margin-bottom:0}
.vh-sol .b-acc{display:grid;gap:12px}
.vh-sol .b-q{border:1px solid var(--line);border-radius:18px;background:#fff;transition:border-color .3s,box-shadow .3s}
.vh-sol .b-q[open]{border-color:rgba(0,174,239,.5);box-shadow:0 20px 40px -30px rgba(106,27,154,.4)}
.vh-sol .b-q summary{list-style:none;cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:20px;padding:22px 26px;font-weight:600;font-size:17px;color:var(--ink)}
.vh-sol .b-q summary::-webkit-details-marker{display:none}
.vh-sol .b-q .pm{width:32px;height:32px;border-radius:50%;background:var(--cool,#F5F8FF);display:grid;place-items:center;flex:none;color:var(--purple);transition:transform .4s var(--ease),background .3s,color .3s}
.vh-sol .b-q .pm svg{width:16px;height:16px}
.vh-sol .b-q[open] .pm{transform:rotate(45deg);background:var(--purple);color:#fff}
.vh-sol .b-a{padding:0 26px 24px;color:var(--ink-2);font-size:15.5px;animation:rise .5s var(--ease)}
@media(max-width:900px){.vh-sol .b-faq-grid{grid-template-columns:1fr}.vh-sol .b-faq .sec-head{position:static}}

.vh-sol .b-ctas{display:flex;gap:16px;justify-content:center;flex-wrap:wrap;margin-top:44px}
.vh-sol .cta-inner .b-ctas .btn{margin-top:0}
.vh-sol .cta-inner .b-outline-w{background:transparent;color:#fff;border:1.5px solid rgba(255,255,255,.6);box-shadow:none}
.vh-sol .cta-inner .b-outline-w:hover{background:rgba(255,255,255,.12);transform:translateY(-2px)}
.vh-sol .cta-inner .b-outline-w::after{display:none}
@media(max-width:520px){.vh-sol .hero .ctas .btn,.vh-sol .b-ctas .btn{width:100%}}
`;

/* ---------- Page behaviour: hero demo, scroll reveal, workflow auto-advance, FAQ ---------- */
function usePageInteractions() {
  useEffect(() => {
    const cleanups = [];
    const timers = new Set();
    const st = (f, t) => { const id = setTimeout(() => { timers.delete(id); f(); }, t); timers.add(id); return id; };
    const si = (f, t) => { const id = setInterval(f, t); cleanups.push(() => clearInterval(id)); return id; };
    /* eslint-disable */
      const reduce=matchMedia('(prefers-reduced-motion: reduce)').matches;
      const $=s=>document.querySelector(s);

      /* hero: rotating BFSI role setup */
      const ROLES=[
        ['Relationship Manager','Business',[['Customer communication',35],['Product knowledge',30],['Compliance awareness',20],['Resume fit',15]],[0,1,3,4]],
        ['Software Engineer','Technology',[['Java',35],['System design',25],['SQL',20],['Resume fit',20]],[0,2,3,4]],
        ['Claims Handler','Business',[['Claims assessment',35],['Customer empathy',25],['Fraud awareness',25],['Resume fit',15]],[0,1,3,4]],
        ['KYC Analyst','Business',[['KYC and AML knowledge',40],['Attention to detail',25],['Risk judgment',20],['Resume fit',15]],[0,1,3,4]],
        ['Data Engineer','Technology',[['Python',30],['Data pipelines',30],['SQL',25],['Resume fit',15]],[0,2,3,4]]
      ];
      const valBox=$('#roleVal'), type=$('#roleType'), sk=$('#skills'), tgs=[...document.querySelectorAll('#toggles .tg')];
      sk.innerHTML=[0,1,2,3].map(()=>'<div class="b-sk"><span></span><span class="bar"><b></b></span><em>0%</em></div>').join('');
      const rows=[...sk.children];
      function apply(i){
        const r=ROLES[i];
        type.textContent=r[1]; type.classList.toggle('tech',r[1]==='Technology');
        rows.forEach((row,j)=>{row.children[0].textContent=r[2][j][0];const b=row.querySelector('b');b.style.width='0';
          st(()=>{b.style.width=(r[2][j][1]*2.2)+'%';row.querySelector('em').textContent=r[2][j][1]+'%';},120+j*110);});
        tgs.forEach((t,j)=>st(()=>t.classList.toggle('on',r[3].includes(j)),reduce?0:j*110));
      }
      apply(0); let ci=0;
      if(!reduce) si(()=>{
        const old=valBox.querySelector('em'); ci=(ci+1)%ROLES.length;
        const nu=document.createElement('em'); nu.textContent=ROLES[ci][0]; nu.className='pre'; valBox.appendChild(nu);
        requestAnimationFrame(()=>{old.classList.add('out'); nu.getBoundingClientRect(); nu.className='';});
        st(()=>old.remove(),600); apply(ci);
      },3600);

      /* reveal */
      const io=new IntersectionObserver(es=>es.forEach(e=>{if(!e.isIntersecting)return;e.target.classList.add('in');io.unobserve(e.target);
        if(e.target.id==='wf') startWF();
      }),{threshold:.15,rootMargin:'0px 0px -60px 0px'});
      document.querySelectorAll('.rv,.stagger').forEach(el=>io.observe(el));
      cleanups.push(()=>io.disconnect());

      /* workflow auto-advance */
      const sts=[...document.querySelectorAll('.st')], line=$('#wfLine');
      function startWF(){
        let k=0; const vertical=()=>innerWidth<=980;
        const step=()=>{sts.forEach((s,j)=>s.classList.toggle('on',j===k));
          const f=k/(sts.length-1); line.style.transform=vertical()?`scaleY(${f})`:`scaleX(${f})`; k=(k+1)%sts.length;};
        if(reduce){sts.forEach(s=>s.classList.add('on'));line.style.transform='none';return;}
        step(); si(step,1800);
      }

      /* FAQ: one open at a time */
      const qs=[...document.querySelectorAll('.b-q')];
      qs.forEach(q=>q.addEventListener('toggle',()=>{if(q.open) qs.forEach(o=>{if(o!==q) o.open=false;});}));
    /* eslint-enable */
    return () => { cleanups.forEach((f) => f()); timers.forEach(clearTimeout); };
  }, []);
}

/* ============================== PAGE ============================== */
export default function BfsiSolutionPage() {
  useEffect(() => {
    document.title = "AI Interviews for BFSI Hiring | Viriqo Hires";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = "Hire for banking, financial services and insurance with AI interviews, coding assessments and interview monitoring for technical and non-technical roles.";
    /* smooth anchor scrolling (was html{scroll-behavior:smooth} in the HTML), restored on unmount */
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "smooth";
    return () => { document.documentElement.style.scrollBehavior = prev; };
  }, []);

  usePageInteractions();

  return (
    <>
      <style>{CSS}</style>
      <div className="vh-sol">
        <main>
          <section className="hero b-hero">
            <div className="blob" />
            <div className="blob b2" />
            <div className="wrap hero-grid">
              <div>
                <span className="eyebrow fade-in" style={{ animationDelay: ".1s" }}>Solutions for BFSI</span>
                <h1 className="fade-in" style={{ animationDelay: ".25s" }}>
                  AI Interviews for
                  {' '}
                  <span className="grad">Banking, Financial Services and Insurance</span>
                  {' '}
                  Hiring
                </h1>
                <p className="lead fade-in" style={{ animationDelay: ".45s" }}>
                  Structured, verifiable interviews for every BFSI role, from software engineers to relationship managers, in one platform.
                </p>
                <div className="ctas fade-in" style={{ animationDelay: ".6s" }}>
                  <a href="/#demo-cta" className="btn btn-primary">Book a Demo{' '}<span className="arr">→</span></a>
                  {' '}
                  <a href="#how" className="btn btn-ghost">See How It Works{' '}<span className="arr">→</span></a>
                </div>
                <div className="pills fade-in" style={{ animationDelay: ".8s" }}>
                  <span className="pill"><i />Technical and Non-Technical Roles</span>
                  <span className="pill"><i />Interview Integrity</span>
                  <span className="pill"><i />Reviewable Evidence</span>
                </div>
              </div>
              <div className="fade-in" style={{ animationDelay: ".5s" }}>
                <div className="config b-config" aria-hidden="true">
                  <div className="panel">
                    <div className="panel-top"><b>BFSI interview setup</b><span>Configured</span></div>
                    <div className="field">
                      <label>Role</label>
                      <div className="select">
                        <span className="ind-ic">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 10l9-6 9 6M5 10v8M9 10v8M15 10v8M19 10v8M3 21h18" />
                          </svg>
                        </span>
                        <span className="val" id="roleVal"><em>Relationship Manager</em></span>
                        <span className="b-type" id="roleType">Business</span>
                      </div>
                    </div>
                    <div className="field">
                      <label>Skills and weightings</label>
                      <div className="b-skills" id="skills" />
                    </div>
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label>Workflow</label>
                      <div className="toggles" id="toggles">
                        <span className="tg">AI Interview</span>
                        <span className="tg">Scenarios</span>
                        <span className="tg">Coding</span>
                        <span className="tg">Proctoring</span>
                        <span className="tg">Report</span>
                      </div>
                    </div>
                  </div>
                  <div className="floater f1">
                    <span className="d">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
                        <path d="M9 12l2 2 4-4" />
                      </svg>
                    </span>
                    Integrity verified
                  </div>
                  <div className="floater f2">
                    <span className="d">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                        <path d="M14 3v6h6M8 13h8M8 17h5" />
                      </svg>
                    </span>
                    Evidence recorded
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="b-challenge">
            <div className="wrap">
              <div className="sec-head rv">
                <span className="eyebrow">The Challenge</span>
                <h2>Hiring in BFSI carries more risk than most industries.</h2>
                <p className="lead">Every hire touches customer money, customer data or both. That raises the stakes on every decision.</p>
              </div>
              <div className="b-grid4 stagger">
                <article className="b-card">
                  <span className="b-num">01</span>
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />
                    </svg>
                  </span>
                  <h3>Constant hiring volume</h3>
                  <p>Branches and contact centers hire nonstop. Manual screening can't keep up.</p>
                </article>
                <article className="b-card">
                  <span className="b-num">02</span>
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                  </span>
                  <h3>Higher integrity risk</h3>
                  <p>Remote interviews make it hard to confirm who is answering, and with what help.</p>
                </article>
                <article className="b-card">
                  <span className="b-num">03</span>
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3v18M7 21h10M5 7h14" />
                      <path d="M5 7l-3 7a4 4 0 0 0 6 0zM19 7l-3 7a4 4 0 0 0 6 0z" />
                    </svg>
                  </span>
                  <h3>Decisions that must hold up</h3>
                  <p>Inconsistent interviewers lead to decisions you can't explain later.</p>
                </article>
                <article className="b-card">
                  <span className="b-num">04</span>
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
                    </svg>
                  </span>
                  <h3>A tight tech talent market</h3>
                  <p>You compete with tech companies for engineers, data and security talent.</p>
                </article>
              </div>
            </div>
          </section>
          <section className="b-roles">
            <div className="wrap">
              <div className="sec-head center rv">
                <span className="eyebrow">Built for Every BFSI Role</span>
                <h2>One platform for{' '}<span className="grad">technical and non-technical</span>{' '}hiring.</h2>
                <p className="lead">Configure each interview around the skills a role needs, from writing code to handling a difficult customer.</p>
              </div>
              <div className="b-rolegrid stagger">
                <article className="b-rolecard">
                  <div className="b-rolehead">
                    <span className="ic">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
                      </svg>
                    </span>
                    <div>
                      <h3>Technology and Data Roles</h3>
                      <p>AI interviews plus coding assessments.</p>
                    </div>
                  </div>
                  <ul className="b-list">
                    <li>
                      <span className="tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      Software engineers
                    </li>
                    <li>
                      <span className="tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      Data analysts and engineers
                    </li>
                    <li>
                      <span className="tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      Cybersecurity specialists
                    </li>
                    <li>
                      <span className="tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      QA engineers
                    </li>
                    <li>
                      <span className="tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      Cloud and DevOps engineers
                    </li>
                  </ul>
                  <div className="b-tags"><span>AI Interview</span><span>Coding Assessment</span><span>Proctoring</span></div>
                </article>
                <article className="b-rolecard alt">
                  <div className="b-rolehead">
                    <span className="ic">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
                      </svg>
                    </span>
                    <div>
                      <h3>Business and Customer-Facing Roles</h3>
                      <p>Scenario-based AI interviews on judgment and communication.</p>
                    </div>
                  </div>
                  <ul className="b-list">
                    <li>
                      <span className="tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      Relationship managers
                    </li>
                    <li>
                      <span className="tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      Customer service representatives
                    </li>
                    <li>
                      <span className="tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      Collections specialists
                    </li>
                    <li>
                      <span className="tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      Claims and underwriting support
                    </li>
                    <li>
                      <span className="tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      KYC and AML analysts
                    </li>
                    <li>
                      <span className="tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      Loan officers and insurance sales
                    </li>
                  </ul>
                  <div className="b-tags"><span>AI Interview</span><span>Scenario Questions</span><span>Proctoring</span></div>
                </article>
              </div>
              <p className="b-note rv">Viriqo Hires assesses skills. License and background checks stay with your existing process.</p>
            </div>
          </section>
          <section id="how" className="b-how">
            <div className="wrap">
              <div className="sec-head center rv">
                <span className="eyebrow">How It Works</span>
                <h2>From job requirement to a decision you can defend.</h2>
              </div>
              <div className="wf stagger" id="wf">
                <div className="line"><b id="wfLine" /></div>
                <div className="st">
                  <div className="n">
                    <em>01</em>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
                    </svg>
                  </div>
                  <div>
                    <h3>Define</h3>
                    <p>Set skills, weightings and role-specific questions.</p>
                  </div>
                </div>
                <div className="st">
                  <div className="n">
                    <em>02</em>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
                    </svg>
                  </div>
                  <div>
                    <h3>Interview</h3>
                    <p>AI runs the interview with smart follow-ups.</p>
                  </div>
                </div>
                <div className="st">
                  <div className="n">
                    <em>03</em>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
                    </svg>
                  </div>
                  <div>
                    <h3>Assess</h3>
                    <p>Coding tests or real customer scenarios.</p>
                  </div>
                </div>
                <div className="st">
                  <div className="n">
                    <em>04</em>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                  </div>
                  <div>
                    <h3>Verify</h3>
                    <p>Screen, webcam and second-camera monitoring.</p>
                  </div>
                </div>
                <div className="st">
                  <div className="n">
                    <em>05</em>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                      <path d="M14 3v6h6M8 13h8M8 17h5" />
                    </svg>
                  </div>
                  <div>
                    <h3>Evaluate</h3>
                    <p>Scores, transcripts, recordings and reports.</p>
                  </div>
                </div>
              </div>
              <div className="center b-after rv">
                <a href="/features" className="btn btn-ghost">Explore the Platform{' '}<span className="arr">→</span></a>
              </div>
            </div>
          </section>
          <section className="b-caps">
            <div className="wrap">
              <div className="sec-head center rv">
                <span className="eyebrow">What Viriqo Hires Does for BFSI</span>
                <h2>Everything BFSI hiring needs,{' '}<span className="grad">in one workflow.</span></h2>
              </div>
              <div className="b-grid3 stagger">
                <article className="b-cap">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
                    </svg>
                  </span>
                  <h3>AI Interviews</h3>
                  <p>Structured interviews with intelligent follow-up questions.</p>
                </article>
                <article className="b-cap">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
                    </svg>
                  </span>
                  <h3>Skills and Weightings</h3>
                  <p>Define what matters most for each role.</p>
                </article>
                <article className="b-cap">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
                    </svg>
                  </span>
                  <h3>Coding Assessments</h3>
                  <p>Test programming skills inside the interview.</p>
                </article>
                <article className="b-cap">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </span>
                  <h3>Interview Proctoring</h3>
                  <p>Screen, webcam and second camera, with suspicious activity flagged.</p>
                </article>
                <article className="b-cap">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
                    </svg>
                  </span>
                  <h3>Scoring and Reports</h3>
                  <p>Compare every candidate on the same criteria.</p>
                </article>
                <article className="b-cap">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <h3>Role-Based Access</h3>
                  <p>Control who can see candidate data.</p>
                </article>
              </div>
            </div>
          </section>
          <section className="b-trust">
            <div className="wrap b-trust-grid">
              <div className="rv">
                <span className="eyebrow light">Trust and Governance</span>
                <h2>Keep a clear record of every interview.</h2>
                <p className="lead">In a regulated industry, how you decided matters as much as who you hired. Keep the evidence in one place.</p>
                <p className="b-fine">Hiring compliance obligations stay with the employer. Viriqo Hires gives you the records to support them.</p>
              </div>
              <div className="b-govlist stagger">
                <div className="b-gov">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </span>
                  <div>
                    <h3>Reviewable Evidence</h3>
                    <p>Recordings, transcripts and flags stay with each candidate.</p>
                  </div>
                </div>
                <div className="b-gov">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="6" cy="6" r="3" />
                      <circle cx="18" cy="18" r="3" />
                      <path d="M9 6h5a4 4 0 0 1 0 8h-4a4 4 0 0 0 0 8" />
                    </svg>
                  </span>
                  <div>
                    <h3>Audit Trail</h3>
                    <p>Show what was asked, how it was answered and how it was scored.</p>
                  </div>
                </div>
                <div className="b-gov">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 21a8 8 0 0 1 16 0" />
                    </svg>
                  </span>
                  <div>
                    <h3>Human Decisions</h3>
                    <p>AI scores the interview. Your team makes the call.</p>
                  </div>
                </div>
                <div className="b-gov">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <div>
                    <h3>Secure Data Handling</h3>
                    <p>SOC 2 controls, a GDPR-aligned privacy program and US and Canada data residency.</p>
                  </div>
                </div>
                <div className="b-gov">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3v18M7 21h10M5 7h14" />
                      <path d="M5 7l-3 7a4 4 0 0 0 6 0zM19 7l-3 7a4 4 0 0 0 6 0z" />
                    </svg>
                  </span>
                  <div>
                    <h3>Fairness Checks</h3>
                    <p>An independent bias audit has been completed.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="b-scen">
            <div className="wrap">
              <div className="sec-head rv">
                <span className="eyebrow">BFSI Hiring Scenarios</span>
                <h2>Built for the way financial institutions hire.</h2>
              </div>
              <div className="b-scen-grid stagger">
                <article className="b-sc">
                  <span className="b-num">01</span>
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 10l9-6 9 6M5 10v8M9 10v8M15 10v8M19 10v8M3 21h18" />
                    </svg>
                  </span>
                  <h3>Branch and Contact Center Hiring</h3>
                  <p>A consistent first interview for every applicant.</p>
                </article>
                <article className="b-sc">
                  <span className="b-num">02</span>
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 9L12 4 2 9l10 5 10-5z" />
                      <path d="M6 11.5V16c3 2 9 2 12 0v-4.5" />
                    </svg>
                  </span>
                  <h3>Graduate and Campus Programs</h3>
                  <p>Assess large intakes on the same criteria.</p>
                </article>
                <article className="b-sc">
                  <span className="b-num">03</span>
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="5" y="5" width="14" height="14" rx="2" />
                      <path d="M9 9h6v6H9zM9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />
                    </svg>
                  </span>
                  <h3>Technology Teams</h3>
                  <p>Coding assessments and AI interviews for tech talent.</p>
                </article>
                <article className="b-sc">
                  <span className="b-num">04</span>
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
                    </svg>
                  </span>
                  <h3>Multi-Location Hiring</h3>
                  <p>One interview process across every region.</p>
                </article>
                <article className="b-sc">
                  <span className="b-num">05</span>
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 4h18l-7 8v6l-4 2v-8z" />
                    </svg>
                  </span>
                  <h3>Pre-Screening</h3>
                  <p>Shortlist on skills before manager rounds.</p>
                </article>
              </div>
            </div>
          </section>
          <section className="b-teams">
            <div className="wrap">
              <div className="sec-head center rv">
                <span className="eyebrow">Built for Every Team in the Process</span>
                <h2>One platform for every stakeholder.</h2>
              </div>
              <div className="b-grid4 stagger">
                <article className="b-card b-team">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />
                    </svg>
                  </span>
                  <h3>Talent Acquisition</h3>
                  <p>Faster pipelines with automated first interviews.</p>
                </article>
                <article className="b-card b-team">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
                    </svg>
                  </span>
                  <h3>Hiring Managers</h3>
                  <p>Consistent, role-specific evaluations.</p>
                </article>
                <article className="b-card b-team">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                  </span>
                  <h3>Risk and Compliance</h3>
                  <p>Interview records ready whenever a hire is reviewed.</p>
                </article>
                <article className="b-card b-team">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
                    </svg>
                  </span>
                  <h3>Technology Leaders</h3>
                  <p>Skills assessed without pulling engineers into every screen.</p>
                </article>
              </div>
            </div>
          </section>
          <section className="b-band-sec">
            <div className="wrap">
              <div className="b-band rv">
                <div>
                  <h2>See Viriqo Hires with your BFSI roles.</h2>
                  <p>A walkthrough built around the roles you hire most.</p>
                </div>
                <a href="/#demo-cta" className="btn btn-primary">Book a Demo{' '}<span className="arr">→</span></a>
              </div>
            </div>
          </section>
          <section className="b-faq">
            <div className="wrap b-faq-grid">
              <div className="sec-head rv">
                <span className="eyebrow">FAQs</span>
                <h2>Frequently asked questions</h2>
                <p className="lead">Using Viriqo Hires in banking, financial services and insurance.</p>
              </div>
              <div className="b-acc stagger">
                <details className="b-q" open>
                  <summary>
                    Can Viriqo Hires assess non-technical BFSI roles?
                    <span className="pm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </span>
                  </summary>
                  <div className="b-a">
                    <p>
                      Yes. Scenario-based AI interviews cover roles like relationship managers, claims handlers and KYC analysts. Technology roles also get coding assessments.
                    </p>
                  </div>
                </details>
                <details className="b-q">
                  <summary>
                    How does it handle cheating and proxy candidates?
                    <span className="pm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </span>
                  </summary>
                  <div className="b-a">
                    <p>
                      Viriqo Hires monitors screen, webcam and a second camera, and flags activity like tab switching or another person in frame for your team to review.
                    </p>
                  </div>
                </details>
                <details className="b-q">
                  <summary>
                    Does the AI make the hiring decision?
                    <span className="pm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </span>
                  </summary>
                  <div className="b-a">
                    <p>No. Viriqo Hires scores the interview and shows the evidence. Your team decides.</p>
                  </div>
                </details>
                <details className="b-q">
                  <summary>
                    Can we use our own questions?
                    <span className="pm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </span>
                  </summary>
                  <div className="b-a">
                    <p>Yes. Set skills, weightings and questions for each role, including scenarios based on your products and customers.</p>
                  </div>
                </details>
                <details className="b-q">
                  <summary>
                    Does it replace background checks?
                    <span className="pm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </span>
                  </summary>
                  <div className="b-a">
                    <p>No. Background, license and regulatory checks stay with your existing process.</p>
                  </div>
                </details>
                <details className="b-q">
                  <summary>
                    Which regions are supported?
                    <span className="pm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </span>
                  </summary>
                  <div className="b-a">
                    <p>The United States and Canada, with data residency in both.</p>
                  </div>
                </details>
              </div>
            </div>
          </section>
          <section className="final" id="cta">
            <div className="wrap">
              <div className="cta-inner rv">
                <span className="ring r1" />
                <span className="ring r2" />
                <div className="c">
                  <h2>Build a hiring process your BFSI teams can trust.</h2>
                  <p className="lead">Structured interviews and clear evidence for every role you hire.</p>
                  <div className="b-ctas">
                    <a href="/#demo-cta" className="btn btn-primary">Book a Demo{' '}<span className="arr">→</span></a>
                    {' '}
                    <a href="/#demo-cta" className="btn b-outline-w">Talk to Our Team{' '}<span className="arr">→</span></a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}